import type { ProjectDto } from '../dtos/ProjectDto.js';
import { getSupabaseClient } from '../../../../core/supabase/client.js';

const SELECT_FIELDS = `
  id, org_id, slug, name, code, status_metadata_id, description, start_at, end_at,
  budget, target_sessions, client_metadata_id, brand_metadata_id, category_metadata_id,
  created_at, updated_at, created_by,
  status:metadatas!projects_status_metadata_id_fkey(id, name, type),
  client:metadatas!projects_client_metadata_id_fkey(id, name),
  brand:metadatas!projects_brand_metadata_id_fkey(id, name),
  category:metadatas!projects_category_metadata_id_fkey(id, name),
  kpis(count)
`;

interface ProjectRow {
  id: string;
  org_id: string;
  slug: string;
  name: string;
  code: string;
  status_metadata_id?: string | null;
  description?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  budget?: number | null;
  target_sessions?: number | null;
  client_metadata_id?: string | null;
  brand_metadata_id?: string | null;
  category_metadata_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  status?: { id: string; name: string; type: string } | null;
  client?: { id: string; name: string } | null;
  brand?: { id: string; name: string } | null;
  category?: { id: string; name: string } | null;
  kpis?: Array<{ count: number }>;
}

function rowToDto(row: ProjectRow): ProjectDto {
  return {
    id: row.id,
    orgId: row.org_id,
    slug: row.slug,
    name: row.name,
    code: row.code,
    statusMetadataId: row.status_metadata_id ?? null,
    description: row.description ?? null,
    startAt: row.start_at ?? null,
    endAt: row.end_at ?? null,
    budget: row.budget ?? null,
    targetSessions: row.target_sessions ?? null,
    clientMetadataId: row.client_metadata_id ?? null,
    brandMetadataId: row.brand_metadata_id ?? null,
    categoryMetadataId: row.category_metadata_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    status: row.status ?? null,
    client: row.client ?? null,
    brand: row.brand ?? null,
    category: row.category ?? null,
    kpisCount: Array.isArray(row.kpis) ? row.kpis[0]?.count ?? 0 : 0,
  };
}

export class ProjectRemoteDataSource {
  private get supabase() {
    return getSupabaseClient() as any;
  }

  async getProjectById(id: string): Promise<ProjectDto | null> {
    const { data, error } = await this.supabase
      .from('projects')
      .select(SELECT_FIELDS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ? rowToDto(data) : null;
  }

  async listProjects(options?: { orgId?: string; limit?: number; offset?: number }): Promise<{ data: ProjectDto[]; total: number }> {
    let query = this.supabase
      .from('projects')
      .select(SELECT_FIELDS, { count: 'exact' })
      .order('created_at', { ascending: false });

    if (options?.orgId) query = query.eq('org_id', options.orgId);
    if (typeof options?.limit === 'number' && typeof options?.offset === 'number') {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options?.limit === 'number') {
      query = query.limit(options.limit);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: (data ?? []).map(rowToDto), total: count ?? 0 };
  }

  async createProject(payload: {
    orgId: string;
    slug: string;
    name: string;
    code: string;
    createdBy: string;
    description?: string | null;
    statusMetadataId?: string | null;
    startAt?: string | null;
    endAt?: string | null;
    budget?: number | null;
    targetSessions?: number | null;
    clientMetadataId?: string | null;
    brandMetadataId?: string | null;
    categoryMetadataId?: string | null;
  }): Promise<ProjectDto | null> {
    const { data, error } = await this.supabase
      .from('projects')
      .insert({
        org_id: payload.orgId,
        slug: payload.slug,
        name: payload.name,
        code: payload.code,
        created_by: payload.createdBy,
        description: payload.description ?? null,
        status_metadata_id: payload.statusMetadataId ?? null,
        start_at: payload.startAt ?? null,
        end_at: payload.endAt ?? null,
        budget: payload.budget ?? null,
        target_sessions: payload.targetSessions ?? null,
        client_metadata_id: payload.clientMetadataId ?? null,
        brand_metadata_id: payload.brandMetadataId ?? null,
        category_metadata_id: payload.categoryMetadataId ?? null,
      })
      .select(SELECT_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ? rowToDto(data) : null;
  }

  async updateProject(id: string, payload: {
    name?: string;
    code?: string;
    slug?: string;
    description?: string | null;
    statusMetadataId?: string | null;
    startAt?: string | null;
    endAt?: string | null;
    budget?: number | null;
    targetSessions?: number | null;
    clientMetadataId?: string | null;
    brandMetadataId?: string | null;
    categoryMetadataId?: string | null;
  }): Promise<ProjectDto | null> {
    const update: Record<string, any> = {};
    if (payload.name !== undefined) update.name = payload.name;
    if (payload.code !== undefined) update.code = payload.code;
    if (payload.slug !== undefined) update.slug = payload.slug;
    if (payload.description !== undefined) update.description = payload.description;
    if (payload.statusMetadataId !== undefined) update.status_metadata_id = payload.statusMetadataId;
    if (payload.startAt !== undefined) update.start_at = payload.startAt;
    if (payload.endAt !== undefined) update.end_at = payload.endAt;
    if (payload.budget !== undefined) update.budget = payload.budget;
    if (payload.targetSessions !== undefined) update.target_sessions = payload.targetSessions;
    if (payload.clientMetadataId !== undefined) update.client_metadata_id = payload.clientMetadataId;
    if (payload.brandMetadataId !== undefined) update.brand_metadata_id = payload.brandMetadataId;
    if (payload.categoryMetadataId !== undefined) update.category_metadata_id = payload.categoryMetadataId;

    const { data, error } = await this.supabase
      .from('projects')
      .update(update)
      .eq('id', id)
      .select(SELECT_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ? rowToDto(data) : null;
  }

  async deleteProject(id: string): Promise<void> {
    // Routed through RPC (not a bare table delete) so the cascade delete
    // this triggers (project_locations/sessions/formx_*/kpi_values/issues/...)
    // gets one shared request_id stamped on every resulting audit_log row —
    // see 20260730001020_project_functions_delete_cascade_audit.sql.
    const { error } = await this.supabase.rpc('delete_project', { p_project_id: id });
    if (error) throw new Error(error.message);
  }

  async existsBySlug(orgId: string, slug: string, excludeId?: string): Promise<boolean> {
    let query = this.supabase
      .from('projects')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('slug', slug);
    if (excludeId) query = query.neq('id', excludeId);
    const { count, error } = await query;
    if (error) throw new Error(error.message);
    return (count ?? 0) > 0;
  }

  async existsByCode(orgId: string, code: string, excludeId?: string): Promise<boolean> {
    let query = this.supabase
      .from('projects')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .eq('code', code);
    if (excludeId) query = query.neq('id', excludeId);
    const { count, error } = await query;
    if (error) throw new Error(error.message);
    return (count ?? 0) > 0;
  }

  async searchProjects(orgId: string, query: string, limit = 15): Promise<ProjectDto[]> {
    const { data, error } = await this.supabase.rpc('search_projects', {
      p_org_id: orgId,
      search_query: query,
      row_limit: limit,
      row_offset: 0,
    });
    if (error) throw new Error(error.message);
    return (data ?? []).map((p: Partial<ProjectRow>) => rowToDto({
      id: p.id ?? '',
      name: p.name ?? '',
      code: p.code ?? '',
      created_at: p.created_at ?? '',
      updated_at: p.updated_at ?? '',
      description: p.description ?? null,
      start_at: p.start_at ?? null,
      end_at: p.end_at ?? null,
      budget: p.budget ?? null,
      org_id: p.org_id ?? orgId,
      slug: p.slug ?? '',
      status_metadata_id: p.status_metadata_id ?? null,
      client_metadata_id: p.client_metadata_id ?? null,
      brand_metadata_id: p.brand_metadata_id ?? null,
      category_metadata_id: p.category_metadata_id ?? null,
      created_by: p.created_by ?? '',
      status: null, client: null, brand: null, category: null, kpis: [],
    }));
  }
}
